/**
 * Super Admin emailed sign-in codes.
 * ADMIN_EMAIL_2FA is off unless it is 1, true, on, or yes.
 * The browser does not read that flag. It asks the server.
 * On the live project the code goes to the account email.
 * Everywhere else it goes only to delivered+tag@resend.dev.
 */

const CODE_TTL_MS = 10 * 60 * 1000;
const SESSION_TTL_MS = 12 * 60 * 60 * 1000;
const RESEND_INTERVAL_MS = 60 * 1000;
const MAX_ATTEMPTS = 5;
const LIVE_PROJECT_REF = 'zvnubjrizoiwrzrhunjz';
const TEST_RECIPIENT = 'delivered+admin-2fa@resend.dev';
const ADMIN_CODE_SUBJECT = 'Your Pacarion sign-in code';
// WKWebView does not send the cross-site HttpOnly cookie. The native app sends this instead.
const ADMIN_2FA_HEADER = 'X-Pacarion-Admin-2FA';

function adminEmail2faRequired(env) {
  const source = env || {};
  const raw = String(source.ADMIN_EMAIL_2FA || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'on' || raw === 'yes';
}

function isLiveProject(env) {
  const source = env || {};
  const url = String(source.SUPABASE_URL || source.REACT_APP_SUPABASE_URL || '');
  return url.indexOf(LIVE_PROJECT_REF) !== -1;
}

function isResendTestInbox(email) {
  return /^delivered\+[a-z0-9._-]+@resend\.dev$/.test(email);
}

function adminEmailRecipient(email, env) {
  const value = String(email || '').trim().toLowerCase();
  if (!value || value.indexOf('@') < 1 || value.endsWith('@')) return '';
  if (isLiveProject(env)) return value;
  if (isResendTestInbox(value)) return value;
  return TEST_RECIPIENT;
}

function isSixDigitCode(value) {
  return typeof value === 'string' && /^[0-9]{6}$/.test(value);
}

function maskEmail(email) {
  const value = String(email || '').trim().toLowerCase();
  const at = value.indexOf('@');
  if (at < 1 || at >= value.length - 1) return 'your email';
  return `${value.slice(0, 1)}***${value.slice(at)}`;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function adminCodeEmailText(code) {
  const safe = isSixDigitCode(code) ? code : '';
  return [
    `Your Pacarion sign-in code is ${safe}`,
    '',
    'It expires in 10 minutes and can be used once.',
    '',
    'If you did not try to sign in, you can ignore this email.',
  ].join('\n');
}

function digitCells(code) {
  const chars = String(code || '').split('');
  return chars.map((char) => (
    `<td style="width:36px;height:48px;border:1px solid #d2d2d7;border-radius:12px;text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:24px;font-weight:600;color:#1d1d1f;">${escapeHtml(char)}</td>`
  )).join('<td style="width:6px;font-size:0;line-height:0;">&nbsp;</td>');
}

function adminCodeEmailHtml(code) {
  const safe = escapeHtml(isSixDigitCode(code) ? code : '');
  const cells = isSixDigitCode(code) ? digitCells(code) : '';
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(ADMIN_CODE_SUBJECT)}</title>
</head>
<body style="margin:0;padding:0;background:#ffffff;color:#1d1d1f;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;">
    <tr>
      <td align="center" style="padding:32px 20px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:420px;">
          <tr>
            <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:13px;letter-spacing:0.08em;text-transform:uppercase;color:#6e6e73;padding-bottom:16px;">Pacarion</td>
          </tr>
          <tr>
            <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:28px;line-height:1.2;font-weight:600;color:#1d1d1f;padding-bottom:12px;">Sign-in code</td>
          </tr>
          <tr>
            <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:17px;line-height:1.45;color:#3a3a3c;padding-bottom:24px;">Your Pacarion sign-in code is ${safe}</td>
          </tr>
          <tr>
            <td style="padding-bottom:28px;">
              <table role="presentation" cellpadding="0" cellspacing="0"><tr>${cells}</tr></table>
            </td>
          </tr>
          <tr>
            <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:15px;line-height:1.5;color:#6e6e73;">It expires in 10 minutes and can be used once.</td>
          </tr>
          <tr>
            <td style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:15px;line-height:1.5;color:#6e6e73;padding-top:12px;">If you did not try to sign in, you can ignore this email.</td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export {
  CODE_TTL_MS,
  SESSION_TTL_MS,
  RESEND_INTERVAL_MS,
  MAX_ATTEMPTS,
  LIVE_PROJECT_REF,
  TEST_RECIPIENT,
  ADMIN_CODE_SUBJECT,
  ADMIN_2FA_HEADER,
  adminEmail2faRequired,
  isLiveProject,
  isResendTestInbox,
  adminEmailRecipient,
  isSixDigitCode,
  maskEmail,
  adminCodeEmailText,
  adminCodeEmailHtml,
};
